import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Loader2, FilePlus2, Copy, ExternalLink, Check } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const SVC = [
  { v: "private_car", l: "Private Airport Car", opt: "private_car", pax: true },
  { v: "shared_shuttle", l: "Shared Airport Shuttle", pax: true },
  { v: "private_group_shuttle", l: "Private Group Airport Shuttle", pax: true },
  { v: "golf_club", l: "Golf Club Ride" },
  { v: "long_trip", l: "Long Distance Trip", opt: "long_trip_destinations" },
  { v: "local_area", l: "Nearby & Local Areas", opt: "local_areas" },
  { v: "tours", l: "Guided Tour", opt: "tour_packages", pax: true },
];
const input = "w-full rounded-sm bg-input border border-gold/20 px-3 py-2.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-gold";
const label = "block text-xs text-slate-400 mb-1";

export function AdminInvoiceDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [catalog, setCatalog] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", payment_method: "online", note: "" });
  const [lines, setLines] = useState([]);
  const [svc, setSvc] = useState("golf_club");
  const [opt, setOpt] = useState("");
  const [pax, setPax] = useState("");
  const [vehicles, setVehicles] = useState("1");
  const [cDesc, setCDesc] = useState("");
  const [cAmt, setCAmt] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState("");

  useEffect(() => { if (open && !catalog) api.get("/services").then(({ data }) => setCatalog(data)).catch(() => {}); }, [open, catalog]);

  const cur = SVC.find((s) => s.v === svc) || {};
  const optionKeys = catalog && cur.opt ? Object.keys(catalog[cur.opt] || {}) : [];
  const optLabel = (k) => (cur.opt === "tour_packages" ? catalog.tour_packages[k]?.label || k : k.replace(/_/g, " "));
  const subtotal = lines.reduce((s, l) => s + l.amount, 0);

  const addService = async () => {
    if (cur.opt && !opt) return toast.error("Pick an option for this service.");
    if (cur.pax && !pax) return toast.error("Enter passengers.");
    try {
      const { data } = await api.post("/quote", {
        service_type: svc, option: cur.opt ? opt : null,
        passengers: cur.pax ? +pax : null, vehicles: +vehicles || 1,
      });
      if (!data || !data.amount) return toast.error("Couldn't price this — use a custom line instead.");
      setLines((p) => [...p, { description: data.description, amount: data.amount }]);
      setOpt(""); setPax(""); setVehicles("1");
    } catch { toast.error("Pricing failed — try a custom line."); }
  };

  const addCustom = () => {
    const a = +cAmt;
    if (!cDesc.trim() || !a || a <= 0) return toast.error("Enter a description and a positive amount.");
    setLines((p) => [...p, { description: cDesc.trim(), amount: +a.toFixed(2) }]);
    setCDesc(""); setCAmt("");
  };

  const create = async () => {
    if (!form.name || !form.email || !form.phone) return toast.error("Fill customer name, email and phone.");
    if (!lines.length) return toast.error("Add at least one line item.");
    setBusy(true);
    try {
      const { data } = await api.post("/admin/invoices", { ...form, items: lines });
      setResult(data);
      toast.success("Draft invoice created!");
      onCreated && onCreated();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not create invoice."); }
    finally { setBusy(false); }
  };

  const reset = () => { setLines([]); setResult(null); setForm({ name: "", email: "", phone: "", payment_method: "online", note: "" }); };
  const copy = (t) => { navigator.clipboard.writeText(t); setCopied(t); toast.success("Link copied!"); setTimeout(() => setCopied(""), 1500); };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild>
        <button data-testid="new-invoice-btn" className="inline-flex items-center gap-2 rounded-full bg-gold px-4 py-2 text-sm font-semibold text-navy hover:bg-gold-light transition-colors duration-200"><FilePlus2 className="h-4 w-4" /> New draft invoice</button>
      </DialogTrigger>
      <DialogContent className="bg-navy-light border-gold/30 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="text-white font-heading">Create a draft invoice</DialogTitle>
        <DialogDescription className="text-slate-400 text-sm">Add customer details and line items to generate a shareable invoice and payment link. HST 15% applies to online card payments only.</DialogDescription></DialogHeader>

        {result ? (
          <div className="space-y-4" data-testid="invoice-result">
            <div className="rounded-sm bg-navy border border-gold/20 p-4">
              <div className="flex justify-between text-sm"><span className="text-slate-400">Subtotal</span><span className="text-white">${result.subtotal.toFixed(2)}</span></div>
              {result.tax_amount > 0 && <div className="flex justify-between text-sm"><span className="text-slate-400">HST 15%</span><span className="text-white">${result.tax_amount.toFixed(2)}</span></div>}
              <div className="flex justify-between text-sm border-t border-gold/20 mt-2 pt-2"><span className="text-slate-300 font-medium">Total</span><span className="text-gold font-bold">${result.amount.toFixed(2)}</span></div>
            </div>
            {[["Invoice (view / print)", result.invoice_url], ["Pay link (send to customer)", result.pay_url]].map(([lbl, url]) => (
              <div key={lbl}>
                <div className={label}>{lbl}</div>
                <div className="flex items-center gap-2">
                  <input readOnly value={url} className={`${input} text-xs`} data-testid={`result-${lbl.includes("Pay") ? "pay" : "invoice"}-url`} />
                  <button onClick={() => copy(url)} className="shrink-0 rounded-sm border border-gold/40 p-2.5 text-gold hover:bg-gold/10">{copied === url ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}</button>
                  <a href={url} target="_blank" rel="noopener noreferrer" className="shrink-0 rounded-sm border border-gold/40 p-2.5 text-gold hover:bg-gold/10"><ExternalLink className="h-4 w-4" /></a>
                </div>
              </div>
            ))}
            <button onClick={reset} className="w-full rounded-full border border-gold/40 py-2.5 text-sm font-semibold text-gold hover:bg-gold/10">Create another</button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div><label className={label}>Customer name</label><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="inv-name" className={input} /></div>
              <div><label className={label}>Email</label><input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="inv-email" className={input} /></div>
              <div><label className={label}>Phone</label><input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="inv-phone" className={input} /></div>
            </div>
            <div>
              <label className={label}>Payment method (HST 15% is added on online card only)</label>
              <Select value={form.payment_method} onValueChange={(v) => setForm({ ...form, payment_method: v })}>
                <SelectTrigger data-testid="inv-method" className="bg-input border-gold/20 text-white"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="online">Pay online by card (+HST)</SelectItem>
                  <SelectItem value="e_transfer">Interac e-Transfer</SelectItem>
                  <SelectItem value="pay_later">Pay on arrival</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className={label}>Customer note / itinerary (optional — shown on the invoice)</label>
              <textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="inv-note" rows={4}
                placeholder={"e.g. To keep everyone comfortable, we'll provide 2 vehicles (up to 6 passengers each).\n5:00 PM pickup at ... → drop off at ...\nAlternative contact: ..."}
                className={`${input} resize-y`} />
            </div>

            {/* Add service line */}
            <div className="rounded-sm bg-navy border border-gold/20 p-3 space-y-2">
              <div className="text-xs uppercase tracking-wider text-gold/80">Add a service (auto-priced)</div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <Select value={svc} onValueChange={(v) => { setSvc(v); setOpt(""); }}>
                  <SelectTrigger data-testid="inv-svc" className="bg-input border-gold/20 text-white text-xs col-span-2"><SelectValue /></SelectTrigger>
                  <SelectContent>{SVC.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
                </Select>
                {cur.opt && (
                  <Select value={opt} onValueChange={setOpt}>
                    <SelectTrigger data-testid="inv-opt" className="bg-input border-gold/20 text-white text-xs col-span-2"><SelectValue placeholder="Option" /></SelectTrigger>
                    <SelectContent>{optionKeys.map((k) => <SelectItem key={k} value={k} className="capitalize">{optLabel(k)}</SelectItem>)}</SelectContent>
                  </Select>
                )}
                {cur.pax && <input type="number" min="1" value={pax} onChange={(e) => setPax(e.target.value)} placeholder="Pax" data-testid="inv-pax" className={input} />}
                <input type="number" min="1" max="10" value={vehicles} onChange={(e) => setVehicles(e.target.value)} placeholder="Vehicles" data-testid="inv-vehicles" className={input} />
              </div>
              <button onClick={addService} data-testid="inv-add-service" className="inline-flex items-center gap-1.5 text-sm font-semibold text-gold hover:underline"><Plus className="h-4 w-4" /> Price & add</button>
            </div>

            {/* Add custom line */}
            <div className="rounded-sm bg-navy border border-gold/20 p-3 space-y-2">
              <div className="text-xs uppercase tracking-wider text-gold/80">Or add a custom line</div>
              <div className="flex gap-2">
                <input value={cDesc} onChange={(e) => setCDesc(e.target.value)} placeholder="Description" data-testid="inv-custom-desc" className={input} />
                <input type="number" min="0" step="0.01" value={cAmt} onChange={(e) => setCAmt(e.target.value)} placeholder="$ Amount" data-testid="inv-custom-amt" className={`${input} w-28`} />
                <button onClick={addCustom} data-testid="inv-add-custom" className="shrink-0 rounded-sm border border-gold/40 px-3 text-gold hover:bg-gold/10"><Plus className="h-4 w-4" /></button>
              </div>
            </div>

            {/* Lines */}
            {lines.length > 0 && (
              <div className="space-y-1" data-testid="inv-lines">
                {lines.map((l, i) => (
                  <div key={i} className="flex items-center justify-between gap-2 rounded-sm bg-navy border border-gold/15 px-3 py-2 text-sm">
                    <span className="text-white truncate">{l.description}</span>
                    <span className="flex items-center gap-2 shrink-0"><span className="text-gold font-semibold">${l.amount.toFixed(2)}</span>
                      <button onClick={() => setLines((p) => p.filter((_, x) => x !== i))} className="text-slate-500 hover:text-red-400"><Trash2 className="h-4 w-4" /></button></span>
                  </div>
                ))}
                <div className="flex justify-between text-sm pt-1"><span className="text-slate-400">Subtotal (pre-tax)</span><span className="text-white font-semibold" data-testid="inv-subtotal">${subtotal.toFixed(2)}</span></div>
              </div>
            )}

            <button onClick={create} disabled={busy} data-testid="inv-create-btn" className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-gold py-3 font-semibold text-navy hover:bg-gold-light disabled:opacity-60 transition-colors duration-200">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FilePlus2 className="h-4 w-4" />} Create draft invoice
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
